import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Percent,
  ShoppingBag,
  Store,
  BadgePercent,
  LayoutDashboard,
  MapPin,
  ChefHat,
  BarChart3,
  Quote,
  Handshake,
} from "lucide-react";

const AMBER = "text-amber-500";

const SlideShell = ({ eyebrow, number, children }) => (
  <div className="relative h-full w-full flex flex-col justify-center max-w-6xl mx-auto px-6 sm:px-12 lg:px-20 py-24">
    <span className="pointer-events-none select-none absolute -top-4 right-4 sm:right-10 font-display font-bold text-[26vw] sm:text-[18vw] leading-none text-white/[0.04]">
      {number}
    </span>
    {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
    {children}
  </div>
);

const Point = ({ children }) => (
  <li className="flex items-start gap-3 text-sm sm:text-lg text-gray-300 leading-relaxed">
    <span className="mt-2 w-2 h-2 rounded-full bg-amber-500 shrink-0" />
    <span>{children}</span>
  </li>
);

const Stat = ({ value, label }) => (
  <div className="rounded-2xl bg-[#171923] border border-amber-500/20 p-5 sm:p-7">
    <div className="font-display text-2xl sm:text-5xl font-semibold text-amber-500">{value}</div>
    <div className="mt-2 font-mono-x text-[10px] sm:text-xs tracking-[0.2em] uppercase text-gray-500">{label}</div>
  </div>
);

const IconBadge = ({ icon: Icon }) => (
  <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 mb-5">
    <Icon className="w-6 h-6" />
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-display font-semibold text-white tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl">
    {children}
  </h2>
);

const SLIDES = [
  {
    id: "cover",
    render: () => (
      <SlideShell eyebrow="Sales Briefing · Coding In Store" number="">
        <p className="font-mono-x text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gray-500 mb-6">
          Your Online Store — live product walkthrough
        </p>
        <h1 className="font-display font-semibold text-white tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl">
          Your online <span className="italic text-amber-500">store</span>
        </h1>
        <p className="mt-7 max-w-2xl text-base sm:text-xl text-gray-400 leading-relaxed">
          Direct online ordering, order management, delivery tracking and analytics for independent
          food businesses — with zero commission.
        </p>
        <p className="mt-10 font-mono-x text-[10px] sm:text-xs tracking-[0.25em] uppercase text-gray-600">
          Use arrow keys or the on-screen controls to present
        </p>
      </SlideShell>
    ),
  },
  {
    id: "problem",
    render: () => (
      <SlideShell eyebrow="The Problem" number="01">
        <IconBadge icon={Percent} />
        <H2>
          Aggregators eat <span className="italic text-amber-500">your margin.</span>
        </H2>
        <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-4xl">
          <Stat value="~30%" label="Commission taken per order" />
          <Stat value="$0" label="Customer data you get back" />
          <Stat value="1 tap" label="For a customer to switch to a rival" />
        </div>
        <ul className="mt-9 space-y-3 max-w-2xl">
          <Point>Marketplace apps charge up to a third of every sale — before food cost, wages or rent.</Point>
          <Point>They own the customer relationship, the data and the re-order.</Point>
          <Point>Your shop is one logo in a list of competitors on someone else's platform.</Point>
        </ul>
      </SlideShell>
    ),
  },
  {
    id: "product",
    render: () => (
      <SlideShell eyebrow="The Product" number="02">
        <IconBadge icon={ShoppingBag} />
        <H2>
          Your own store. <span className="italic text-amber-500">Your own customers.</span>
        </H2>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-gray-400 leading-relaxed">
          Coding In Store gives every shop a branded online ordering website — the same experience
          customers already use today on the live demo store:
        </p>
        <ul className="mt-8 space-y-3 max-w-2xl">
          <Point>Customers browse the full menu with photos, add items to the cart and customise orders.</Point>
          <Point>Secure card payment at checkout — no cash handling at the door.</Point>
          <Point>Prices shown GST-inclusive, in your local currency, on your own domain name.</Point>
          <Point>Allergen guide built in, so dietary questions answer themselves.</Point>
        </ul>
      </SlideShell>
    ),
  },
  {
    id: "proof",
    render: () => (
      <SlideShell eyebrow="Live Proof" number="03">
        <IconBadge icon={Store} />
        <H2>
          See online demo at <span className="italic text-amber-500">onlinekebab.store</span>
        </H2>
        <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-4xl">
          <Stat value="15–25 min" label="Quoted pickup time" />
          <Stat value="10 km" label="Free-delivery radius" />
          <Stat value="$40+" label="Free-delivery threshold" />
        </div>
        <ul className="mt-9 space-y-3 max-w-2xl">
          <Point>Pickup and delivery promises are set by the shop — and shown to customers up front.</Point>
          <Point>Free-delivery rules (radius + minimum spend) drive bigger baskets automatically.</Point>
          <Point>Every claim on this slide is visible on the working store, not a mock-up.</Point>
        </ul>
      </SlideShell>
    ),
  },
  {
    id: "deals",
    render: () => (
      <SlideShell eyebrow="Promotions Engine" number="04">
        <IconBadge icon={BadgePercent} />
        <H2>
          Deals that <span className="italic text-amber-500">sell themselves.</span>
        </H2>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-gray-400 leading-relaxed">
          Promotions are configured by the shop — no developer, no waiting. Live examples on the demo store:
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
          {[
            { t: "Buy one, second can free", d: "2nd free can-drink deal running as a hot deal banner." },
            { t: "10% off doner kebabs", d: "Item-level percentage discounts, switched on in minutes." },
            { t: "$7.50 small meal deal", d: "Small chips + can of drink bundled with any kebab." },
            { t: "$9.00 large meal deal", d: "Medium chips + 600ml drink bundled with any kebab." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl bg-[#171923] border border-amber-500/20 p-5 sm:p-6">
              <div className="font-display text-lg sm:text-xl font-semibold text-amber-400">{c.t}</div>
              <div className="mt-2 text-sm text-gray-400 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  {
    id: "orders",
    render: () => (
      <SlideShell eyebrow="Order Management" number="05">
        <IconBadge icon={LayoutDashboard} />
        <H2>
          A dashboard that keeps up with <span className="italic text-amber-500">the rush.</span>
        </H2>
        <ul className="mt-9 space-y-3 max-w-2xl">
          <Point>Live order feed with an audio chime the moment an order lands.</Point>
          <Point>One-tap status updates: accepted, preparing, ready, out for delivery.</Point>
          <Point>Prep timers keep the kitchen honest at peak hour.</Point>
          <Point>Thermal receipt printing — the order is on the pass before the phone rings.</Point>
        </ul>
      </SlideShell>
    ),
  },
  {
    id: "delivery",
    render: () => (
      <SlideShell eyebrow="Delivery & Tracking" number="06">
        <IconBadge icon={MapPin} />
        <H2>
          Customers watch their food <span className="italic text-amber-500">arrive.</span>
        </H2>
        <ul className="mt-9 space-y-3 max-w-2xl">
          <Point>Every order gets a live "track my order" page — no more "where's my food?" phone calls.</Point>
          <Point>Assign deliveries to your own drivers with automated routing.</Point>
          <Point>Delivery zones and minimum-spend rules enforced automatically at checkout.</Point>
          <Point>Driver distance and payout reports at the end of every night.</Point>
        </ul>
      </SlideShell>
    ),
  },
  {
    id: "menu-analytics",
    render: () => (
      <SlideShell eyebrow="Menu Builder & Analytics" number="07">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <IconBadge icon={ChefHat} />
            <h3 className="font-display text-2xl sm:text-4xl font-semibold text-white leading-tight">
              Menu changes in <span className="italic text-amber-500">two taps</span>
            </h3>
            <ul className="mt-6 space-y-3">
              <Point>Update a price or mark an item sold-out — live everywhere in seconds.</Point>
              <Point>Combo deals and modifiers built visually, no code.</Point>
            </ul>
          </div>
          <div>
            <IconBadge icon={BarChart3} />
            <h3 className="font-display text-2xl sm:text-4xl font-semibold text-white leading-tight">
              Know your busiest hour <span className="italic text-amber-500">before it hits</span>
            </h3>
            <ul className="mt-6 space-y-3">
              <Point>Daily and monthly revenue breakdowns.</Point>
              <Point>Peak-hour heatmaps, top dishes and repeat-customer metrics.</Point>
            </ul>
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    id: "reviews",
    render: () => (
      <SlideShell eyebrow="What Customers Say" number="08">
        <IconBadge icon={Quote} />
        <H2>
          Real reviews from the <span className="italic text-amber-500">live store.</span>
        </H2>
        <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-5xl">
          {[
            "Ordered a HSP and a kebab. Both super delicious, very juicy tasty meat and very generous portions.",
            "Best decision ever. Outstanding customer service, ridiculously fresh ingredients.",
            "The complete experience was a huge adventure and was pretty amazing.",
            "Really great kebab and even better service!",
          ].map((q, i) => (
            <div key={i} className="rounded-2xl bg-[#171923] border border-amber-500/20 p-5 sm:p-7">
              <Quote className="w-5 h-5 text-amber-500 mb-3" />
              <p className="font-display italic text-base sm:text-xl text-gray-200 leading-relaxed">“{q}”</p>
            </div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  {
    id: "close",
    render: () => (
      <SlideShell eyebrow="Next Steps" number="09">
        <IconBadge icon={Handshake} />
        <H2>
          Let's put your shop <span className="italic text-amber-500">online.</span>
        </H2>
        <ul className="mt-9 space-y-3 max-w-2xl">
          <Point>Zero commission — you keep every dollar of every order.</Point>
          <Point>Setup and menu onboarding handled for you.</Point>
          <Point>Live and taking orders in days, not months.</Point>
        </ul>
        <div className="mt-12 rounded-2xl bg-[#171923] border border-amber-500/25 p-6 sm:p-8 max-w-2xl">
          <p className="font-mono-x text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gray-500 mb-4">
            Talk to us today
          </p>
          <p className="font-display text-xl sm:text-3xl font-semibold text-white">0450 405 757</p>
          <p className="mt-2 font-display text-xl sm:text-3xl font-semibold text-amber-500">salesperson@codingin.store</p>
          <p className="mt-4 text-sm text-gray-500">Coding In Store · Redcliffe, QLD 4020</p>
        </div>
      </SlideShell>
    ),
  },
];

export default function SalesDeck() {
  const [index, setIndex] = useState(0);
  const total = SLIDES.length;

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowRight", "ArrowDown", " ", "PageDown"].includes(e.key)) {
        e.preventDefault();
        next();
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        setIndex(0);
      } else if (e.key === "End") {
        setIndex(total - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, total]);

  const Slide = SLIDES[index].render;

  return (
    <div
      data-testid="sales-deck"
      className="grain fixed inset-0 bg-[#0b0c10] overflow-hidden select-none"
    >
      <div className="pointer-events-none absolute -top-48 -right-48 w-[600px] h-[600px] rounded-full bg-amber-500/10 blur-[160px]" />

      <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-5 sm:px-10 h-[76px]">
        <div className="flex items-center gap-3" data-testid="deck-brand">
        <button
          data-testid="brand-logo"
              onClick={() => window.open("https://codingin.store", "_blank", "noopener,noreferrer")}
          className="flex items-center group"
    aria-label="Coding In Store — home"
  >
          <img src="/logo-header.png" alt="Coding In Store" className="h-10 w-auto mr-3 rounded-md bg-amber-500 border border-amber-500/25" />
          <span className="hidden sm:block font-display text-base font-semibold text-white leading-none">
            Coding <span className="italic text-amber-500">In Store</span>
            <span className="block font-mono-x text-[9px] tracking-[0.25em] text-gray-500 mt-1 font-normal pt-2">SALES BRIEFING</span>
          </span>
          </button>
        </div>
        <div data-testid="deck-counter" className="font-mono-x text-xs tracking-[0.3em] text-gray-500">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
      </div>

      <div className="absolute top-0 left-0 right-0 h-[3px] bg-white/5 z-30">
        <motion.div
          data-testid="deck-progress"
          className="h-full bg-amber-500"
          animate={{ width: `${((index + 1) / total) * 100}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>

      <div className="absolute inset-0 pt-[76px] pb-[88px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={SLIDES[index].id}
            data-testid={`slide-${SLIDES[index].id}`}
            className="h-full w-full overflow-y-auto"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <Slide />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-0 inset-x-0 z-20 flex items-center justify-between px-5 sm:px-10 h-[88px]">
        <div className="flex gap-2" data-testid="deck-dots">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              data-testid={`deck-dot-${s.id}`}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                i === index ? "w-8 bg-amber-500" : "w-2 bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="deck-prev-button"
            onClick={prev}
            disabled={index === 0}
            aria-label="Previous slide"
            className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="deck-next-button"
            onClick={next}
            disabled={index === total - 1}
            aria-label="Next slide"
            className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-[#0b0c10] disabled:opacity-30 shadow-[0_0_24px_rgba(245,158,11,0.35)] transition-colors duration-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
